import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from "react-helmet-async";
import { FaChevronDown, FaChevronUp, FaSearch } from 'react-icons/fa';
import './FAQPage.css';

const FAQPage = () => {
  const [openItems, setOpenItems] = useState({});
  const [searchTerm, setSearchTerm] = useState('');

  const faqCategories = [
    {
      category: 'General Questions',
      icon: '❓',
      questions: [
        {
          q: 'What is ZustandTech?',
          a: 'ZustandTech is a global software development company specializing in web development, mobile apps, AI solutions, cloud computing, SaaS development, and enterprise software services.'
        },
        {
          q: 'When was ZustandTech founded?',
          a: 'ZustandTech was founded in 2020 with a mission to help startups and businesses build scalable digital products using modern technologies.'
        },
        {
          q: 'Where is ZustandTech located?',
          a: 'ZustandTech is headquartered in India and provides software development services globally.'
        },
        {
          q: 'What industries do you serve?',
          a: 'We serve healthcare, fintech, education, e-commerce, real estate, manufacturing, logistics, and enterprise businesses worldwide.'
        }
      ]
    },
    {
      category: 'Services & Pricing',
      icon: '💰',
      questions: [
        {
          q: 'What services do you offer?',
          a: 'We provide React.js development, Node.js backend systems, AI development, cloud infrastructure, SaaS development, mobile app development, cybersecurity, and enterprise software solutions.'
        },
        {
          q: 'How much do your services cost?',
          a: 'Pricing depends on project scope, timeline, and technical requirements. We offer fixed-price, hourly, and dedicated team engagement models.'
        },
        {
          q: 'Do you offer maintenance and support?',
          a: 'Yes, we provide long-term maintenance, upgrades, monitoring, and technical support for all software projects.'
        },
        {
          q: 'What is your project timeline?',
          a: 'Project timelines vary depending on complexity. Small projects may take 4–6 weeks while enterprise applications can take several months.'
        }
      ]
    },
    {
      category: 'Development Process',
      icon: '⚙️',
      questions: [
        {
          q: 'What development methodology do you use?',
          a: 'We follow Agile development methodology with iterative releases, continuous feedback, testing, and transparent collaboration.'
        },
        {
          q: 'How do you ensure software quality?',
          a: 'We use code reviews, automated testing, QA validation, performance optimization, and security testing to ensure high-quality applications.'
        },
        {
          q: 'Can I track project progress?',
          a: 'Yes. We provide regular demos, project updates, and access to project management tools for complete transparency.'
        },
        {
          q: 'What happens after project delivery?',
          a: 'After deployment we provide training, documentation, maintenance, updates, and ongoing technical support.'
        }
      ]
    },
    {
      category: 'Technical Questions',
      icon: '💻',
      questions: [
        {
          q: 'What technologies do you use?',
          a: 'We use React.js, Next.js, Node.js, Python, Flutter, React Native, MongoDB, PostgreSQL, AWS, Azure, TensorFlow, and modern cloud technologies.'
        },
        {
          q: 'Do clients receive source code ownership?',
          a: 'Yes, clients receive full ownership of source code, documentation, and project assets.'
        },
        {
          q: 'How do you handle security?',
          a: 'We follow industry-standard security practices including encryption, secure APIs, authentication systems, GDPR compliance, and security audits.'
        },
        {
          q: 'Can you integrate with existing systems?',
          a: 'Yes, we specialize in API integrations, ERP integrations, CRM integrations, payment systems, and enterprise software connectivity.'
        }
      ]
    },
    {
      category: 'Client Collaboration',
      icon: '🤝',
      questions: [
        {
          q: 'How do we communicate during projects?',
          a: 'We use Slack, Zoom, Microsoft Teams, email, Jira, and Trello for transparent communication and project tracking.'
        },
        {
          q: 'Do you sign NDAs?',
          a: 'Yes, we sign NDAs and maintain strict confidentiality for all client projects.'
        },
        {
          q: 'Do you offer dedicated developers?',
          a: 'Yes, businesses can hire dedicated React developers, Node.js developers, AI engineers, and full-stack development teams.'
        },
        {
          q: 'What information do you need to start a project?',
          a: 'We begin with a discovery session to understand business goals, requirements, features, timelines, and technical expectations.'
        }
      ]
    }
  ];

  const toggleItem = (categoryIndex, questionIndex) => {
    const key = `${categoryIndex}-${questionIndex}`;

    setOpenItems(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const filteredFaqs = faqCategories
    .map(category => ({
      ...category,
      questions: category.questions.filter(
        q =>
          q.q.toLowerCase().includes(searchTerm.toLowerCase()) ||
          q.a.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }))
    .filter(category => category.questions.length > 0);

  return (
    <>
      <Helmet>

        {/* PRIMARY SEO */}
        <title>
          FAQ | ZustandTech - Software Development Company
        </title>

        <meta
          name="description"
          content="Frequently asked questions about ZustandTech software development services including React.js development, AI solutions, mobile app development, cloud computing, SaaS platforms, pricing, timelines, and support."
        />

        <meta
          name="keywords"
          content="
            software development FAQ,
            react development company,
            AI development services,
            mobile app development company,
            custom software development,
            cloud computing services,
            SaaS development company,
            enterprise software solutions,
            ZustandTech FAQ
          "
        />

        <meta name="robots" content="index, follow" />

        <link
          rel="canonical"
          href="https://www.zustandtech.com/faq"
        />

        {/* OPEN GRAPH */}
        <meta
          property="og:title"
          content="FAQ | ZustandTech Software Development Services"
        />

        <meta
          property="og:description"
          content="Frequently asked questions about web development, AI solutions, mobile apps, SaaS platforms, cloud systems, and enterprise software."
        />

        <meta
          property="og:image"
          content="https://www.zustandtech.com/ar.png"
        />

        <meta
          property="og:url"
          content="https://www.zustandtech.com/faq"
        />

        <meta property="og:type" content="website" />

        {/* TWITTER SEO */}
        <meta name="twitter:card" content="summary_large_image" />

        <meta
          name="twitter:title"
          content="FAQ | ZustandTech"
        />

        <meta
          name="twitter:description"
          content="Frequently asked questions about software development services, AI solutions, cloud computing, and mobile app development."
        />

        {/* FAQ SCHEMA */}
        <script type="application/ld+json">
          {`
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "What services does ZustandTech provide?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "ZustandTech provides web development, mobile app development, AI solutions, cloud computing, SaaS development, and enterprise software services."
                }
              },
              {
                "@type": "Question",
                "name": "Does ZustandTech provide AI development services?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, ZustandTech develops AI-powered applications, machine learning systems, automation software, and intelligent business solutions."
                }
              },
              {
                "@type": "Question",
                "name": "What technologies does ZustandTech use?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "We use React.js, Node.js, Next.js, Python, Flutter, AWS, Azure, MongoDB, TensorFlow, and modern cloud technologies."
                }
              }
            ]
          }
          `}
        </script>

      </Helmet>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="faq-page"
      >

        {/* HERO */}
        <section className="faq-search-section">
          <div className="container">

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="page-heading"
            >

              <h1>
                Frequently Asked
                <span className="gradient-text"> Questions</span>
              </h1>

              <p>
                Find answers about our software development services,
                AI solutions, cloud computing, SaaS platforms,
                and enterprise applications.
              </p>

            </motion.div>

            <div className="faq-search-container">

              <FaSearch className="search-icon" />

              <input
                type="text"
                placeholder="Search questions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="faq-search-input"
              />

            </div>

          </div>
        </section>

        {/* SEO CONTENT */}
        <section className="faq-seo-content">
          <div className="container">

            <h2>
              Software Development FAQ
            </h2>

            <p>
              ZustandTech is a global software development company
              specializing in React.js development, Node.js backend
              systems, AI solutions, mobile app development,
              cloud computing, SaaS platforms, and enterprise software.
            </p>

            <h2>
              Why Businesses Choose ZustandTech
            </h2>

            <p>
              Our expert team delivers scalable web applications,
              AI-powered systems, cloud-native infrastructure,
              and custom software solutions for startups and enterprises worldwide.
            </p>

          </div>
        </section>

        {/* FAQ CONTENT */}
        <section className="faq-content">
          <div className="container">

            {filteredFaqs.map((category, categoryIndex) => (

              <motion.div
                key={categoryIndex}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: categoryIndex * 0.1 }}
                viewport={{ once: true }}
                className="faq-category"
              >

                <h2 className="category-title">
                  <span className="category-icon">
                    {category.icon}
                  </span>

                  {category.category}
                </h2>

                <div className="faq-list">

                  {category.questions.map((item, questionIndex) => {

                    const key = `${categoryIndex}-${questionIndex}`;

                    const isOpen = openItems[key];

                    return (

                      <div key={questionIndex} className="faq-item">

                        <button
                          className={`faq-question ${isOpen ? 'open' : ''}`}
                          onClick={() =>
                            toggleItem(categoryIndex, questionIndex)
                          }
                        >

                          <span>{item.q}</span>

                          {isOpen ? (
                            <FaChevronUp />
                          ) : (
                            <FaChevronDown />
                          )}

                        </button>

                        <motion.div
                          initial={false}
                          animate={{
                            height: isOpen ? 'auto' : 0
                          }}
                          transition={{ duration: 0.3 }}
                          className="faq-answer-wrapper"
                        >

                          <div className="faq-answer">
                            <p>{item.a}</p>
                          </div>

                        </motion.div>

                      </div>
                    );
                  })}

                </div>

              </motion.div>
            ))}

            {filteredFaqs.length === 0 && (

              <div className="no-results">

                <h3>No questions found</h3>

                <p>
                  Try searching with different keywords
                </p>

              </div>
            )}

          </div>
        </section>

        {/* CONTACT */}
        <section className="faq-contact">
          <div className="container">

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="contact-card"
            >

              <h2>Still Have Questions?</h2>

              <p>
                Contact our team for software development,
                AI solutions, mobile app development,
                and cloud consulting services.
              </p>

              <div className="contact-options">

                <Link
                  to="/contact"
                  className="btn btn-primary"
                >
                  Contact Us
                </Link>

                <a
                  href="mailto:support@zustandtech.com"
                  className="btn btn-outline"
                >
                  Email Support
                </a>

              </div>

            </motion.div>

          </div>
        </section>

      </motion.div>
    </>
  );
};

export default FAQPage;